import { createReducer, on } from '@ngrx/store';
import * as LoadActions from '../actions/load.actions';
import * as CustomerActions from '../actions/customer.actions';
import * as FedexFacilityActions from '../actions/fedex-facility.actions';
import * as LoadOptionsActions from '../actions/load-options.actions';
import { Load } from '../models/load.model';
import { Customer } from '../models/customer.model';
import { FedexFacility } from '../models/fedex-facility.model';
import { FedexAvrMap } from '../models/fedex-avr.model';
import { LoadOptions } from '../models/load-options.model';

export interface SpotsState {
  loads: Load[]; // List of all loads
  selectedLoad?: Load; // Load that is currently selected/being viewed
  customers: Customer[]; // List of all customers
  fedexFacilities: FedexFacility[]; // List of all FedEx facilities
  fedexAvrs: FedexAvrMap; // FedEx AVRs mapping
  loadOptions: LoadOptions | null; // Available load priorities and types
}

export const initialState: SpotsState = {
  loads: [],
  customers: [],
  fedexFacilities: [],
  fedexAvrs: {},
  loadOptions: null
};

export const spotsReducer = createReducer(
  initialState,

  // Load Loads
  on(LoadActions.loadLoadsSuccess, (state, { loads }) => {
    console.log('🔧 Reducer: loadLoadsSuccess triggered');
    console.log('🔧 Reducer: Loads count:', loads?.length);
    if (loads && loads.length > 0) {
      console.log('🔧 Reducer: Sample load from API:', loads[0]);
    }
    return { ...state, loads: loads || [] };
  }),

  // Load Load by ID
  on(LoadActions.loadLoadSuccess, (state, { load }) => ({ ...state, selectedLoad: load })),
  on(LoadActions.clearSelectedLoad, (state) => ({ ...state, selectedLoad: undefined })),

  // Add Load
  on(LoadActions.addLoadSuccess, (state, { load }) => ({
    ...state,
    loads: [load, ...state.loads]
  })),

  // Add Loads in bulk
  on(LoadActions.addLoadsBulkSuccess, (state, { loads }) => ({
    ...state,
    loads: [...loads, ...state.loads]
  })),

  // Update Load
  on(LoadActions.updateLoadSuccess, (state, { load }) => {    
    
    // If you update the load from dashboard, then the selected load will be null and as such will not be updated
    return {
      ...state,
      loads: state.loads.map(l => String(l.id) === String(load.id) ? load : l),
      selectedLoad: state.selectedLoad?.id && String(state.selectedLoad.id) === String(load.id) ? load : state.selectedLoad
    };
  }),

  // Delete Load
  on(LoadActions.deleteLoadSuccess, (state, { loadId }) => ({
    ...state,
    loads: state.loads.filter(l => String(l.id) !== String(loadId)),
    selectedLoad: state.selectedLoad?.id && String(state.selectedLoad.id) === String(loadId) ? undefined : state.selectedLoad
  })),

  // Load Customers
  on(CustomerActions.loadCustomersSuccess, (state, { customers }) => ({ ...state, customers: customers || [] })),

  // Add Customer
  on(CustomerActions.addCustomerSuccess, (state, { customer }) => ({
    ...state,
    customers: [customer, ...state.customers]
  })),

  // Update Customer
  on(CustomerActions.updateCustomerSuccess, (state, { customer }) => ({
    ...state,
    customers: state.customers.map(c => c.id === customer.id ? customer : c)
  })),

  // Delete Customer
  on(CustomerActions.deleteCustomerSuccess, (state, { customerId }) => ({
    ...state,
    customers: state.customers.filter(c => c.id !== customerId)
  })),

  // Load FedEx Facilities
  on(FedexFacilityActions.loadFedexFacilitiesSuccess, (state, { facilities }) => ({ ...state, fedexFacilities: facilities || [] })),

  // Add FedEx Facility
  on(FedexFacilityActions.addFedexFacilitySuccess, (state, { facility }) => ({
    ...state,
    fedexFacilities: [facility, ...state.fedexFacilities]
  })),

  // Update FedEx Facility
  on(FedexFacilityActions.updateFedexFacilitySuccess, (state, { facility }) => ({
    ...state,
    fedexFacilities: state.fedexFacilities.map(f => f.id === facility.id ? facility : f)
  })),

  // Delete FedEx Facility
  on(FedexFacilityActions.deleteFedexFacilitySuccess, (state, { facilityId }) => ({
    ...state,
    fedexFacilities: state.fedexFacilities.filter(f => f.id !== facilityId)
  })),

  // Update FedEx AVR (now part of customer management)
  on(CustomerActions.updateFedexAvrSuccess, (state, { fedexAvr, key }) => {
    const updatedAvrs = { ...state.fedexAvrs };
    // Simply update the key with the value (could be empty object for delete)
    // UI will filter out empty/deleted values
    updatedAvrs[key] = fedexAvr;
    
    return {
      ...state,
      fedexAvrs: updatedAvrs
    };
  }),

  // Load Options
  on(LoadOptionsActions.loadLoadOptionsSuccess, (state, { loadOptions }) => { 
    // Convert fedexAvrs array to map with composite keys if needed
    let fedexAvrsMap: FedexAvrMap = {};
    if (loadOptions.fedexAvrs) {
      if (Array.isArray(loadOptions.fedexAvrs)) {
        loadOptions.fedexAvrs.forEach(avr => {
          const key = `${avr.customerId}-${avr.fedexFacilityId}`;
          fedexAvrsMap[key] = avr;
        });
      } else {
        // Already a map, but ensure keys are in the right format
        fedexAvrsMap = loadOptions.fedexAvrs;
      }
    }
    
    return { 
      ...state, 
      loadOptions,
      fedexAvrs: fedexAvrsMap
    };
  }),
);
